import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import { Modal } from '../../../../components/Modal';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { personnelApi } from '../personnelApi';
import type { PersonnelListItem } from '../personnelTypes';
import { personDisplayName, usePersonNames, type PersonNames } from '../usePersonNames';

/**
 * Who works in one organizational unit, opened from that unit's kebab on the chart.
 *
 * **It lives in the personnel feature, not the organization one**, because its contents are
 * personnel records: the list contract, the identity resolve, the four ways a name can fail
 * to render, and the route a row opens are all personnel's to own. The chart supplies a unit
 * id and a name to put in the title, and knows nothing else about what comes back.
 *
 * The table is a `<DataTablePanel>` — a table inside another surface is exactly what that
 * shell is for, and it brings the whole standard contract: search, sort, resize and paging,
 * all server-side through the same `ListQuery` every list uses. **No Create.** A person is
 * not created from inside an org unit; they are created on Personnel and appointed to a post,
 * which is what puts them here — so the panel is given no `onCreate` and draws no button
 * rather than one that would open the wrong form.
 */
export function UnitEmployeesModal({
  open,
  onClose,
  unit,
}: {
  open: boolean;
  onClose: () => void;
  /** The unit whose employees these are. The name is for the title; the id does the work. */
  unit: { id: string; name: string };
}) {
  const navigate = useNavigate();

  /*
    `useCallback`-stable, or the panel's effect re-runs on every render. Keyed on the unit id
    so opening the modal from a different node is a new request rather than the previous
    node's rows under a new title.
  */
  const fetchPage = useCallback(
    (
      query: { search?: string; page: number; pageSize: number; sort?: string },
      signal: AbortSignal,
    ) =>
      personnelApi.list(
        {
          search: query.search ?? '',
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          unit: unit.id,
          // The resting state hides deleted records, like every other list in the product.
          status: 'active',
        },
        signal,
      ),
    [unit.id],
  );

  const table = useDataTable<PersonnelListItem>({
    fetchPage,
    enabled: open,
    // The employee number: the server's sort allow-list is that and `createdAt`.
    defaultSort: 'employeeNumber',
  });

  /*
    One batch resolve for the page on screen — the composition rule, not a convenience. The
    rows come back from `rsys_` carrying `personId`s and no names, because no name is ever
    copied into that database.
  */
  // A record above this reader's access level arrives with NO personId — that absence is the
  // mask — so there is nothing to resolve, and the name cell says so on its own.
  const pageIds = useMemo(
    () => table.rows.map((row) => row.personId).filter((id): id is string => id != null),
    [table.rows],
  );
  const names = usePersonNames(pageIds);

  const columns = buildColumns(names, (id) => {
    onClose();
    navigate(`/personnel/${id}`);
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Employees — ${unit.name}`}
      size="3xl"
      id="unit-employees-modal"
      // The table owns the scroll, so its search strip and its pager stay put.
      bodyScroll={false}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <DataTablePanel
          id="unit-employees"
          state={table}
          columns={columns}
          rowKey={(personnel) => personnel.id}
          /*
            Named for what it actually matches. The server searches the employee number and
            nothing else — a name lives in `rsys_pii` and matching one would mean copying it
            here — so a bare "Search employees" would promise a search this list cannot do.
          */
          searchPlaceholder="Search employee numbers"
          // Nothing can be done to a selection from here, so the gutter would be a dead control.
          selectable={false}
          emptyMessage="No one is assigned to this unit yet."
          emptySearchMessage="No employee number matches that search."
          minWidth={860}
          fill
        />
      </div>
    </Modal>
  );
}

/**
 * Columns are built per render because the anchor reads resolved names, which arrive after
 * the rows do.
 */
function buildColumns(
  names: PersonNames,
  open: (id: string) => void,
): DataColumn<PersonnelListItem>[] {
  return [
    {
      key: 'name',
      header: 'Name',
      width: 240,
      /*
        NOT sortable, and the shell renders a plain header rather than a dead button. The
        values live in `rsys_pii`; ordering by one would mean resolving the whole table before
        paging it, and offering a control the server would ignore reads as a broken one.
      */
      render: (personnel) => (
        /*
          The anchor: the row's click target, opening the 201 file rather than an edit form.
          `text-slate-800` + `font-medium` with an underline on hover — a clickable thing is
          black in this product, never a link colour.
        */
        <button
          type="button"
          onClick={() => open(personnel.id)}
          className="block w-full truncate text-left font-medium text-slate-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        >
          {personDisplayName(personnel, names)}
        </button>
      ),
    },
    {
      key: 'employeeNumber',
      header: 'Employee no.',
      width: 150,
      sortable: true,
      render: (personnel) => <TextCell value={personnel.employeeNumber} mono />,
    },
    {
      key: 'positionTitle',
      header: 'Position',
      width: 220,
      render: (personnel) => <TextCell value={personnel.positionTitle} />,
    },
    {
      key: 'itemNumber',
      header: 'Item',
      width: 140,
      render: (personnel) => <TextCell value={personnel.itemNumber} mono />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (personnel) =>
        personnel.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];
}
